/**
 * SMS duty-toggle adapter.
 *
 * Wires the shared, presentational `DutyToggleSection` from `@tiaki/profile-ui`
 * to SMS's duty data layer — the SMS analog of AIM's `ProfileDutyToggle`
 * (`apps/aegix-aim-react/src/modules/profileComponent/ProfileDutyToggle.tsx`).
 * The shared section owns the UI and the period→ISO math; this adapter owns
 * the network mutation. Unlike AIM there is no redux mirror — SMS has no
 * navbar duty indicator, so the fetched query state drives the toggle
 * directly.
 *
 * Gating mirrors AIM: rendered only when
 * `permissionDutyStatus() && permissionCanChangeDutyStatus()`. When ungated
 * the component renders nothing — the toggle self-hides for users/roles
 * lacking either duty permission.
 *
 * The duty-row pk comes from `useUser<SMSUser>().user_duty_id`
 * (threaded through the login/`/me/` payload, exactly like AIM reads
 * `userDetail?.user_duty_id`) — no separate full-user-record fetch just for
 * the id. `is_on_duty` from the same session seeds the toggle's initial
 * value until the duty query resolves. When the user lacks the permissions
 * we pass `null` to the fetch so the query stays disabled regardless.
 */
import { useTranslation } from 'react-i18next';
import { useUser } from '@aegix/auth-identity';
import { DutyToggleSection } from '@tiaki/profile-ui';
import type { SMSUser } from '../../lib/sms-auth';
import { useDutyStatus } from '../../hooks/queries/useDuty';
import {
  permissionCanChangeDutyStatus,
  permissionDutyStatus,
} from '../../utils/permission-list';

export function ProfileDutyToggle() {
  const { t } = useTranslation();
  const user = useUser<SMSUser>();
  const dutyStatusPermission = permissionDutyStatus();
  const canChangeDutyStatus = permissionCanChangeDutyStatus();

  // Only address the duty row when permitted (mirrors AIM); null keeps the
  // query disabled. Source is the auth session, not a separate user fetch.
  const dutyUserId =
    dutyStatusPermission && canChangeDutyStatus
      ? (user?.user_duty_id ?? null)
      : null;

  const { dutyStatus, updateDuty } = useDutyStatus(dutyUserId);

  const handleToggleOn = () => {
    updateDuty.mutate({ is_on_duty: true, scheduled_at: null });
  };

  const handleScheduleOff = (untilISO: string) => {
    updateDuty.mutate({ is_on_duty: false, scheduled_at: untilISO });
  };

  // Gate matches AIM's `dutyStatusPermission && canChangeDutyStatus`.
  if (!dutyStatusPermission || !canChangeDutyStatus) return null;

  // Seed from the session's `is_on_duty` until the duty query resolves, then
  // prefer the fetched value (the authoritative, post-mutation source).
  const isOnDuty = dutyStatus
    ? !!dutyStatus.is_on_duty
    : !!user?.is_on_duty;

  return (
    <DutyToggleSection
      isOnDuty={isOnDuty}
      onToggleOn={handleToggleOn}
      onScheduleOff={handleScheduleOff}
      disabled={updateDuty.isPending}
      title={t('duty_status', 'Duty status')}
      e2eTitle="profileDutyStatusSection"
    />
  );
}

export default ProfileDutyToggle;
